import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthSession, AuthUser, PermissionCode, RoleCode } from '../features/auth/types/auth';

interface AuthState {
  user?: AuthUser;
  accessToken?: string;
  refreshToken?: string;
  rehydrated: boolean;
  login: (session: AuthSession) => void;
  logout: () => void;
  updateUser: (user: AuthUser) => void;
  isAuthenticated: () => boolean;
  hasRole: (roles: RoleCode | RoleCode[]) => boolean;
  hasPermission: (permissions: PermissionCode | PermissionCode[]) => boolean;
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: undefined,
      accessToken: undefined,
      refreshToken: undefined,
      rehydrated: true,
      login: (session) =>
        set({
          user: session.user,
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          rehydrated: true
        }),
      logout: () => set({ user: undefined, accessToken: undefined, refreshToken: undefined, rehydrated: true }),
      updateUser: (user) => set({ user }),
      isAuthenticated: () => Boolean(get().accessToken && get().user),
      hasRole: (roles) => {
        if (!get().user) return false;
        const roleList = Array.isArray(roles) ? roles : [roles];
        return Boolean(get().user?.roles?.some((role) => roleList.includes(role)));
      },
      hasPermission: (permissions) => {
        if (!get().user) return false;
        const required = Array.isArray(permissions) ? permissions : [permissions];
        return required.every((perm) => Boolean(get().user?.permissions?.includes(perm)));
      }
    }),
    {
      name: 'tkte-auth-store',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken
      })
    }
  )
);

export default useAuthStore;
